import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper,
  Alert, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button,
  MenuItem, Select, FormControl, InputLabel, SelectChangeEvent,
  Tooltip as MuiTooltip, Tabs, Tab,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import { apiService } from '../../services/api';
import CustomReport from './CustomReport';

const COLORS = ['#1976d2', '#7b1fa2', '#388e3c', '#f57c00', '#d32f2f', '#0288d1', '#512da8', '#00796b'];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  return {
    label: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
    year: d.getFullYear(),
    month: d.getMonth(),
  };
});

const Reports: React.FC = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New joiners month selector
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    Promise.all([
      apiService.getEmployees().catch(() => []),
      apiService.getLeaveRequests().catch(() => []),
      apiService.getDepartments().catch(() => []),
      apiService.getPositions().catch(() => []),
    ]).then(([emps, lvs, depts, pos]) => {
      setEmployees(emps);
      setLeaves(lvs);
      setDepartments(depts);
      setPositions(pos);
      setLoading(false);
    }).catch(() => { setError('Failed to load data'); setLoading(false); });
  }, []);

  // Headcount by department
  const headcountByDept = departments.map(d => ({
    name: d.name,
    count: employees.filter((e: any) => e.departmentId === d.id && e.employmentStatus === 'active').length,
  })).filter(d => d.count > 0);

  // Employment status breakdown
  const statusData = [
    { name: 'Active', value: employees.filter((e: any) => e.employmentStatus === 'active').length },
    { name: 'On Leave', value: employees.filter((e: any) => e.employmentStatus === 'on_leave').length },
    { name: 'Terminated', value: employees.filter((e: any) => e.employmentStatus === 'terminated').length },
  ].filter(s => s.value > 0);

  // Leave by status
  const leaveStatusData = [
    { name: 'Approved', value: leaves.filter((l: any) => l.status === 'approved').length },
    { name: 'Pending', value: leaves.filter((l: any) => l.status === 'pending').length },
    { name: 'Rejected', value: leaves.filter((l: any) => l.status === 'rejected').length },
  ].filter(s => s.value > 0);

  // Monthly hires (last 6 months)
  const monthlyHires = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
    const count = employees.filter((e: any) => {
      if (!e.startDate) return false;
      const start = new Date(e.startDate);
      return start.getMonth() === d.getMonth() && start.getFullYear() === d.getFullYear();
    }).length;
    return { month: label, hires: count };
  });

  // New joiners for selected month
  const newJoiners = employees
    .filter((e: any) => {
      if (!e.startDate) return false;
      const start = new Date(e.startDate);
      return start.getFullYear() === selectedYear && start.getMonth() === selectedMonth;
    })
    .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  const getDeptName = (deptId: string) => departments.find(d => d.id === deptId)?.name || '—';
  const getPosName = (posId: string) => positions.find(p => p.id === posId)?.title || '—';

  const selectedMonthLabel = new Date(selectedYear, selectedMonth, 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' });

  const handleMonthChange = (e: SelectChangeEvent<string>) => {
    const opt = MONTH_OPTIONS.find(o => o.label === e.target.value);
    if (opt) { setSelectedYear(opt.year); setSelectedMonth(opt.month); }
  };

  const exportCSV = () => {
    const headers = ['Full Name', 'Email', 'Department', 'Position', 'Employment Type', 'Start Date', 'Status'];
    const rows = newJoiners.map((e: any) => [
      e.fullName || `${e.firstName} ${e.lastName}`,
      e.email || '',
      getDeptName(e.departmentId),
      getPosName(e.positionId),
      e.employmentType || '',
      e.startDate ? new Date(e.startDate).toLocaleDateString('en-GB') : '',
      e.employmentStatus || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map((v: string) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `new-joiners-${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={2}>Reports & Analytics</Typography>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChartIcon />} iconPosition="start" label="Overview" />
        <Tab icon={<TableChartIcon />} iconPosition="start" label="Custom Report Builder" />
      </Tabs>

      {activeTab === 1 && <CustomReport />}

      {activeTab === 0 && <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary KPIs */}
      <Grid container spacing={2} mb={4}>
        {[
          { label: 'Total Headcount', value: employees.filter((e: any) => e.employmentStatus === 'active').length, color: '#1976d2' },
          { label: 'Leave Requests (YTD)', value: leaves.length, color: '#7b1fa2' },
          { label: 'Pending Leaves', value: leaves.filter((l: any) => l.status === 'pending').length, color: '#f57c00' },
          { label: 'Departments', value: departments.length, color: '#388e3c' },
        ].map(({ label, value, color }) => (
          <Grid key={label} size={{ xs: 6, md: 3 }}>
            <Card sx={{ borderLeft: `4px solid ${color}` }}>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── NEW JOINERS SECTION ── */}
      <Paper sx={{ p: 3, mb: 4, border: '1px solid #e3f2fd', borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleAltIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">New Joiners</Typography>
            <Chip
              label={`${newJoiners.length} ${newJoiners.length === 1 ? 'person' : 'people'}`}
              color="primary"
              size="small"
              sx={{ ml: 1 }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonthLabel}
                label="Month"
                onChange={handleMonthChange}
              >
                {MONTH_OPTIONS.map(o => (
                  <MenuItem key={o.label} value={o.label}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <MuiTooltip title="Export CSV for payroll">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportCSV}
                  disabled={newJoiners.length === 0}
                >
                  Export CSV
                </Button>
              </span>
            </MuiTooltip>
          </Box>
        </Box>

        {newJoiners.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">No new joiners in {selectedMonthLabel}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newJoiners.map((e: any, idx: number) => (
                  <TableRow key={e.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {e.fullName || `${e.firstName} ${e.lastName}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{e.email || '—'}</Typography>
                    </TableCell>
                    <TableCell>{getDeptName(e.departmentId)}</TableCell>
                    <TableCell>{getPosName(e.positionId)}</TableCell>
                    <TableCell>
                      <Chip
                        label={e.employmentType || 'Full-time'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="primary.main">
                        {e.startDate ? new Date(e.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={e.employmentStatus === 'active' ? 'Active' : e.employmentStatus === 'on_leave' ? 'On Leave' : 'Terminated'}
                        color={e.employmentStatus === 'active' ? 'success' : e.employmentStatus === 'on_leave' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* ── END NEW JOINERS ── */}

      <Grid container spacing={3}>
        {/* Headcount by Department */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Headcount by Department</Typography>
            {headcountByDept.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>No data yet</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={headcountByDept}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1976d2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Employment Status */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Employment Status</Typography>
            {statusData.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>No data yet</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                    {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Monthly Hires */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Monthly Hires (Last 6 Months)</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthlyHires}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="hires" stroke="#1976d2" strokeWidth={2} dot={{ fill: '#1976d2' }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Leave by Type */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Leave by Status</Typography>
            {leaveStatusData.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>No leave requests yet</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={leaveStatusData} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ name, value }: { name?: string; value?: number }) => `${name ?? ''}: ${value ?? 0}`}>
                    {leaveStatusData.map((_, i) => <Cell key={i} fill={['#388e3c','#f57c00','#d32f2f'][i]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
      </>}
    </Box>
  );
};

export default Reports;
